import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { reportsService } from '../reportsService';
import { ReportPrintOptions, RunReportDto } from '../../../types/reports';

export function useReports() {
  return useQuery({
    queryKey: ['reports'],
    queryFn: () => reportsService.getReports(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The saved print layout for one report. Shared server-side, so every
 * workstation prints the same document.
 */
export function useReportPrintOptions(reportId: string | null) {
  return useQuery({
    queryKey: ['reports', 'print-options', reportId],
    queryFn: () => reportsService.getPrintOptions(reportId as string),
    enabled: Boolean(reportId),
    staleTime: 5 * 60 * 1000,
  });
}

/** Save the shared print layout for a report. */
export function useSaveReportPrintOptions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, options }: { id: string; options: ReportPrintOptions }) =>
      reportsService.savePrintOptions(id, options),
    onSuccess: (response, variables) => {
      // Seed the query with the server's normalised copy so the UI (and the
      // PDF / Print buttons' enabled state) updates without a refetch.
      queryClient.setQueryData(['reports', 'print-options', variables.id], response);
    },
    meta: { successMessage: 'Print options saved' },
  });
}

export function useRunReport() {
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: RunReportDto }) => reportsService.runReport(id, dto),
    // The page renders inline errors/empty states itself, so suppress the global toast.
    meta: { hideToast: true },
  });
}

/** Generate + download a report file (pdf | excel | csv). */
export function useExportReport() {
  return useMutation({
    mutationFn: async ({
      id,
      format,
      filters,
    }: {
      id: string;
      format: string;
      filters: RunReportDto;
    }) => {
      const { blob, filename } = await reportsService.exportReport(id, format, filters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Delay revocation so the download has a chance to start in every browser.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return filename;
    },
    meta: { successMessage: 'Report downloaded' },
  });
}

/** Freeze (or regenerate) the Daily Statement snapshot for a single day. */
export function useGenerateDailyStatement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ date, regenerate }: { date?: string; regenerate?: boolean }) =>
      reportsService.generateDailyStatement(date, regenerate ?? true),
    onSuccess: () => {
      // The archive panel lists frozen snapshots, so refresh it after a save.
      queryClient.invalidateQueries({ queryKey: ['daily-statements'] });
    },
    meta: { successMessage: 'Daily statement saved' },
  });
}

/** The automatic nightly freeze schedule for Daily Statements. */
export function useDailyStatementSchedule(enabled = true) {
  return useQuery({
    queryKey: ['daily-statements', 'schedule'],
    queryFn: () => reportsService.getDailyStatementSchedule(),
    enabled,
    staleTime: 60 * 1000,
  });
}

/** Archived Daily Statement snapshots (newest first), optionally date-scoped. */
export function useDailyStatements(dateFrom?: string, dateTo?: string, enabled = true) {
  return useQuery({
    queryKey: ['daily-statements', 'archive', dateFrom || '', dateTo || ''],
    queryFn: () => reportsService.getDailyStatements(dateFrom, dateTo),
    enabled,
    staleTime: 30 * 1000,
  });
}
